import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  Button, IconButton, Stack, Divider, Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  IEmailTemplate, IEmailTemplatePreview, previewEmailTemplate,
} from "../../../Api/emailTemplatesApi";

const NAVY = "#0F172A";

interface Props {
  template: IEmailTemplate;
  onClose: () => void;
}

/**
 * Preview dialog launched from the template row's preview action.
 * Renders the template against the variable-picker sample values so
 * staff can see exactly what the customer will receive — no lead
 * required.
 */
const TemplatePreviewDialog: React.FC<Props> = ({ template, onClose }) => {
  const [preview, setPreview] = useState<IEmailTemplatePreview | null>(null);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await previewEmailTemplate(template.id, null);
        if (!cancelled) setPreview(p);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Failed to load preview.");
      }
    })();
    return () => { cancelled = true; };
  }, [template.id]);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: NAVY }}>
              Preview · {template.name}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
              Rendered with sample variable values.
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {error ? (
          <Typography sx={{ color: "#dc2626", fontWeight: 700 }}>{error}</Typography>
        ) : !preview ? (
          <Typography sx={{ fontSize: 13, color: "#64748b", fontStyle: "italic" }}>
            Rendering preview…
          </Typography>
        ) : (
          <Stack gap={2}>
            <Box>
              <Typography sx={{ fontSize: 10.5, color: "#94a3b8", letterSpacing: "0.08em", fontWeight: 700 }}>
                SUBJECT
              </Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: NAVY, mt: 0.3 }}>
                {preview.subject || "(empty subject)"}
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography sx={{ fontSize: 10.5, color: "#94a3b8", letterSpacing: "0.08em", fontWeight: 700, mb: 0.7 }}>
                BODY
              </Typography>
              <Box
                sx={{
                  bgcolor: "#fafbfc", borderRadius: 1, p: 2,
                  fontSize: 13.5, color: "#1e293b", lineHeight: 1.55,
                  "& p": { mt: 0, mb: 1 },
                }}
                dangerouslySetInnerHTML={{ __html: preview.body || "<p><em>(empty body)</em></p>" }}
              />
            </Box>

            {template.variables.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: 10.5, color: "#94a3b8", letterSpacing: "0.08em", fontWeight: 700, mb: 0.7 }}>
                  VARIABLES IN USE
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap>
                  {template.variables.map(v => (
                    <Chip key={v} size="small" label={v} sx={{ fontSize: 10.5, fontWeight: 600 }} />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} variant="contained" sx={{
          textTransform: "none", fontWeight: 700,
          bgcolor: NAVY, "&:hover": { bgcolor: "#1e293b" },
        }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplatePreviewDialog;
